/**
 * @fileoverview Popover version of the info card, triggered by click.
 * Reuses layout components from HoverInfoCard.
 * @module shared/components/ui/PopoverInfoCard
 */

import { ReactElement, ReactNode } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/shared/lib/utils'

/**
 * Props for the PopoverInfoCard wrapper.
 */
export interface PopoverInfoCardProps {
  /** Trigger node shown in place; clicks trigger the popover */
  trigger: ReactNode
  /** Rich content displayed inside the popover */
  children: ReactNode
  /** Optional content classes to tweak surface spacing/size */
  contentClassName?: string
  /** Preset width: 'one' = 357px, 'two' = 720px @default 'one' */
  size?: 'one' | 'two'
  /** Aligns the content relative to trigger @default center */
  align?: 'start' | 'center' | 'end'
  /** Side of the trigger to place the card */
  side?: 'top' | 'right' | 'bottom' | 'left'
}

/**
 * Click-triggered version of HoverInfoCard.
 *
 * @component
 */
export function PopoverInfoCard({
  trigger,
  children,
  size = 'one',
  align = 'center',
  side,
  contentClassName,
}: PopoverInfoCardProps): ReactElement {
  const widthClass = size === 'two' ? 'w-[600px]' : 'w-[340px]'
  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align={align}
        {...(side ? { side } : {})}
        className={cn('glass-card !bg-[oklch(from_var(--glass-surface-dark)_l_c_h/0.95)] p-0 max-w-[95vw] overflow-hidden shadow-xl shadow-black/40', widthClass, contentClassName)}
      >
        {children}
      </PopoverContent>
    </Popover>
  )
}
